import auth from '@/auth/auth';
import {
    CreateMultiLinkDocument,
    DeleteProjectSeriesDocument,
    GetProjectSeriesDocument,
    SetSingleProjectDocument,
    UpdateProjectSeriesDocument,
    UpdateProjectSeriesMultilinksDocument,
    UpdateProjectSeriesMutationVariables,
} from '@/generated/graphql';
import fluent from '@/plugins/fluent';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';

export function useProjectSeries(seriesId: Ref<string>) {
    const isAdmin = auth.isAdmin();
    const router = useRouter();

    const { result, loading } = useQuery(
        GetProjectSeriesDocument,
        () => ({ id: toValue(seriesId) }),
        () => ({
            enabled: !!toValue(seriesId),
        }),
    );

    const series = computed(() => result.value?.projectSeries);

    const setSingleProject = useMutation(SetSingleProjectDocument);

    const toggleSingleProject = async () => {
        if (!series.value) return;
        const response = await setSingleProject.mutate({
            id: toValue(seriesId),
            singleProject: !series.value.singleProject,
        });
        if (response?.errors)
            throw new Error(`Could not update project series singleProject value: ${response.errors}`);
    };

    const createProjectButtonLabel = computed(() =>
        series.value?.singleProject ? fluent.$t('single-project-create') : fluent.$t('project-create'),
    );

    const convertButtonLabel = computed(() =>
        series.value?.singleProject ? fluent.$t('allow-multiple-projects') : fluent.$t('limit-to-single-project'),
    );

    const convertDisabledExplanation = computed(() => {
        if (!isAdmin) {
            return fluent.$t('no-permission-to-edit');
        }
        if (!series.value) {
            return fluent.$t('no-loaded-series');
        }
        if (!series.value.singleProject && series.value.projects && series.value.projects.nodes.length > 1) {
            return fluent.$t('series-contains-multiple-events');
        }
        return undefined;
    });

    const deleteProjectSeriesMutation = useMutation(DeleteProjectSeriesDocument);
    async function deleteProjectSeries() {
        if (!series.value) return;
        if (!isAdmin) return;

        if (!confirm(fluent.$t('confirm-delete-project-series', { name: series.value.nameShort }))) return;

        const response = await deleteProjectSeriesMutation.mutate({ seriesId: toValue(seriesId) });
        if (response?.errors) {
            throw Error(`Could not delete project series: ${response.errors}`);
        }
        if (response?.data) {
            router.replace({ name: 'projects' });
        }
    }

    const deleteDisabledExplanation = computed(() => {
        if (!isAdmin) return fluent.$t('no-permission-to-edit');
        if (!series.value) {
            return fluent.$t('no-loaded-project-series');
        }
        if (series.value.projects && series.value.projects.nodes.length > 0)
            return fluent.$t('project-series-has-projects');
        return undefined;
    });

    const createProjectDisabledExplanation = computed(() => {
        if (!isAdmin) {
            return fluent.$t('no-permission-to-create');
        }
        if (!series.value) {
            return fluent.$t('no-loaded-project-series');
        }
        if (series.value.singleProject && series.value.projects && series.value.projects.nodes.length >= 1) {
            return fluent.$t('single-project-limit-reached');
        }
        return undefined;
    });

    const updateProjectSeriesMutation = useMutation(UpdateProjectSeriesDocument);
    async function updateProjectSeries(data: UpdateProjectSeriesMutationVariables) {
        const result = await updateProjectSeriesMutation.mutate(data);
        if (result?.errors) {
            throw Error(`Error on update project series: ${result.errors}`);
        }
    }

    const updateSeriesMultiLinksMutation = useMutation(UpdateProjectSeriesMultilinksDocument);
    async function updateProjectSeriesMultiLinkId(fieldName: string, multiLinkId: string) {
        if (!series.value) return;
        const response = await updateSeriesMultiLinksMutation.mutate({ id: series.value.id, [fieldName]: multiLinkId });
        if (response?.errors) {
            throw new Error(`Could not update project series multilink ${fieldName}: ${response.errors}`);
        }
    }

    const createMultiLinkMutation = useMutation(CreateMultiLinkDocument);
    async function createProjectSeriesMultiLink(fieldName: string) {
        if (!series.value) return;
        const name = `${series.value.id}-${fieldName}`;
        const response = await createMultiLinkMutation.mutate({ name });
        if (response?.errors) {
            throw new Error(`Could not create Multilink for project series: ${response.errors}`);
        }
        if (response?.data) {
            const multiLinkId = response.data.createMultilink?.multilink?.id;
            if (multiLinkId) {
                await updateProjectSeriesMultiLinkId(`${fieldName}Id`, multiLinkId);
                return multiLinkId;
            }
        }
    }

    return {
        series,
        loading,
        createProjectButtonLabel,
        convertButtonLabel,
        convertDisabledExplanation,
        deleteDisabledExplanation,
        createProjectDisabledExplanation,
        toggleSingleProject,
        deleteProjectSeries,
        updateProjectSeries,
        createProjectSeriesMultiLink,
    };
}
